/**
 * Ticket de venta — path de impresión offline (browser print).
 *
 * POS.4 entrega el path de impresión por navegador (`window.print` en una
 * ventana 80mm). Los adaptadores de hardware (Web Bluetooth / USB ESC/POS)
 * se enganchan vía `TicketPrinter` sin tocar el checkout.
 *
 *   browser  → BrowserTicketPrinter (entregado)
 *   bluetooth / usb → deferred (ver createTicketPrinter)
 */

import { formatMoney } from './format';

export interface TicketItem {
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
}

export interface TicketData {
  empresaNombre?: string;
  empresaRif?: string;
  sucursalNombre?: string;
  codigoVenta: string;
  fecha: string;
  clienteNombre?: string;
  vendedorNombre?: string;
  monedaCodigo: string;
  items: TicketItem[];
  subtotal: number;
  iva?: number;
  total: number;
}

/** Identificador del adaptador de impresión preferido. */
export type TicketPrinterKind = 'browser' | 'bluetooth' | 'usb';

/**
 * Contrato de impresora de tickets.
 * Implementaciones futuras de Web Bluetooth / USB deben cumplir esta interfaz
 * y registrarse en `createTicketPrinter` — no inventar drivers fake.
 */
export interface TicketPrinter {
  readonly kind: TicketPrinterKind;
  print(data: TicketData): void | Promise<void>;
}

function buildTicketHtml(data: TicketData): string {
  const itemsHtml = data.items
    .map(
      (item) => `
      <tr>
        <td style="padding: 2px 0;">${item.nombre} x${item.cantidad}</td>
        <td style="text-align: right; padding: 2px 0;">${formatMoney(item.subtotal, data.monedaCodigo)}</td>
      </tr>
    `,
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Ticket ${data.codigoVenta}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body {
            font-family: 'Courier New', Courier, monospace;
            width: 72mm;
            margin: 0 auto;
            padding: 10px 0;
            font-size: 12px;
            color: #000;
          }
          .header { text-align: center; margin-bottom: 8px; }
          .header h2 { margin: 0; font-size: 16px; font-weight: bold; }
          .header p { margin: 2px 0; font-size: 11px; }
          .divider { border-top: 1px dashed #000; margin: 6px 0; }
          table { width: 100%; border-collapse: collapse; }
          .totals { margin-top: 6px; }
          .totals table td { padding: 2px 0; }
          .footer { text-align: center; margin-top: 12px; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${data.empresaNombre || 'MIBODEGA ERP'}</h2>
          <p>${data.empresaRif ? `RIF: ${data.empresaRif}` : ''}</p>
          <p>${data.sucursalNombre || 'Sucursal Principal'}</p>
          <p><strong>Ticket: ${data.codigoVenta}</strong></p>
          <p>Fecha: ${new Date(data.fecha).toLocaleString()}</p>
        </div>
        <div class="divider"></div>
        ${data.clienteNombre ? `<p style="margin:2px 0;">Cliente: ${data.clienteNombre}</p>` : ''}
        ${data.vendedorNombre ? `<p style="margin:2px 0;">Vendedor: ${data.vendedorNombre}</p>` : ''}
        <div class="divider"></div>
        <table>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>
        <div class="divider"></div>
        <div class="totals">
          <table>
            <tr>
              <td>Subtotal:</td>
              <td style="text-align: right;">${formatMoney(data.subtotal, data.monedaCodigo)}</td>
            </tr>
            ${data.iva ? `<tr><td>IVA:</td><td style="text-align: right;">${formatMoney(data.iva, data.monedaCodigo)}</td></tr>` : ''}
            <tr style="font-weight: bold; font-size: 14px;">
              <td style="padding-top: 4px;">TOTAL:</td>
              <td style="text-align: right; padding-top: 4px;">${formatMoney(data.total, data.monedaCodigo)}</td>
            </tr>
          </table>
        </div>
        <div class="divider"></div>
        <div class="footer">
          <p>¡Gracias por su compra!</p>
          <p>Documento No Fiscal</p>
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
    </html>
  `;
}

/**
 * Impresora por defecto: abre una ventana limpia y dispara `window.print()`.
 * Funciona offline (sin red) y no requiere hardware ni permisos especiales
 * más allá de ventanas emergentes.
 */
export class BrowserTicketPrinter implements TicketPrinter {
  readonly kind = 'browser' as const;

  print(data: TicketData): void {
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (!printWindow) {
      alert('Permite ventanas emergentes para imprimir el ticket.');
      return;
    }
    printWindow.document.write(buildTicketHtml(data));
    printWindow.document.close();
  }
}

/**
 * Factory del adaptador de impresión.
 *
 * - `browser` (default): print dialog del navegador — path offline entregado.
 * - `bluetooth` / `usb`: **deferred**. Lanzan error explícito hasta que exista
 *   un adaptador real (Web Bluetooth / WebUSB ESC/POS). No se simulan drivers.
 */
export function createTicketPrinter(kind: TicketPrinterKind = 'browser'): TicketPrinter {
  if (kind === 'browser') {
    return new BrowserTicketPrinter();
  }
  throw new Error(
    `Adaptador de impresión "${kind}" no implementado. ` +
      'POS.4 entrega browser print; Web Bluetooth/USB quedan diferidos. ' +
      'Implementar TicketPrinter y registrar aquí.',
  );
}

/** Singleton del path browser — usado por el checkout y reimpresión. */
const defaultPrinter: TicketPrinter = createTicketPrinter('browser');

/**
 * Genera y abre una ventana limpia para impresión de ticket de 80mm en el navegador.
 * Delega al `BrowserTicketPrinter` (extension point: createTicketPrinter).
 */
export function printThermalTicket(data: TicketData): void {
  void defaultPrinter.print(data);
}

/** @internal test helper — HTML estático del ticket (sin window). */
export function __buildTicketHtmlForTests(data: TicketData): string {
  return buildTicketHtml(data);
}
